import { buildHtmlElement } from '@/js/util/build_html_element';
import { Table as TiptapTable } from '@tiptap/extension-table';
import TiptapTableCell from '@tiptap/extension-table-cell';
import TiptapTableHeader from '@tiptap/extension-table-header';
import TiptapTableRow from '@tiptap/extension-table-row';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import { Extension } from '@tiptap/core';
import { CellSelection } from '@tiptap/pm/tables';
import { TextSelection } from '@tiptap/pm/state';
import { toolbarFor } from './_get_menu_control';
import { colorHex } from './_color_hex';

// A toolbar click runs its transaction while the page has no selection, so anchorNode is null.
const findClosest = (sel) => window.getSelection()?.anchorNode?.parentElement?.closest(sel) ?? null;

const editingTable = (editor) => {
  return editor.isActive('table') && selectedOrFocusedCell();
};

const countSelectedCells = (editor) => {
  const selection = editor.state.selection;

  if (selection instanceof CellSelection) {
    return selection.ranges.length;
  }
  return 0;
};

const canSplitCell = (editor) => {
  const cell = { ...editor.getAttributes('tableCell'), ...editor.getAttributes('tableHeader') };
  const canSplit = cell.colspan > 1 || cell.rowspan > 1;

  return countSelectedCells(editor) <= 1 && canSplit;
};

const willShowCellsMergeMenu = (editor) => {
  const editing = editingTable(editor);
  const manyCellsSelected = countSelectedCells(editor) > 1;
  const mouseIsReleased = !editor.storage.isMousePressed;

  return editing && manyCellsSelected && mouseIsReleased;
};

const willShowCellsSplitMenu = (editor) => {
  const editing = editingTable(editor);
  const cellIsSplitable = canSplitCell(editor);

  return editing && cellIsSplitable;
};

const selectedOrFocusedCell = () => {
  const table = findClosest('table');
  return table?.querySelector('.selectedCell') || findClosest('th, td');
};

const rowMenuVirtualElement = () => {
  const targetedRow = selectedOrFocusedCell()?.parentElement;
  if (!targetedRow) {
    return null;
  }

  return { getBoundingClientRect: () => targetedRow.getBoundingClientRect() };
};

const columnMenuVirtualElement = () => {
  const targetedCell = selectedOrFocusedCell();
  const table = targetedCell?.closest('table');
  if (!table) {
    return null;
  }

  return {
    getBoundingClientRect: () => {
      const cellRect = targetedCell.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();
      return new DOMRect(cellRect.left, tableRect.top, cellRect.width, 0);
    },
  };
};

const cellsSplitMenuVirtualElement = () => {
  const cell = selectedOrFocusedCell();
  if (!cell) {
    return null;
  }

  return { getBoundingClientRect: () => cell.getBoundingClientRect() };
};

const tableConfiguration = {
  resizable: true,
};

const tableRowsMenuConfig = {
  pluginKey: 'tableRowsContextualMenu',
  shouldShow: ({ editor }) => editingTable(editor),
  element: buildHtmlElement('table-rows-menu'),
  getReferencedVirtualElement: rowMenuVirtualElement,
  options: {
    placement: 'left',
    offset: 8,
    flip: {},
  },
};

const tableColumnsMenuConfig = {
  pluginKey: 'tableColumnsContextualMenu',
  shouldShow: ({ editor }) => editingTable(editor),
  element: buildHtmlElement('table-columns-menu'),
  getReferencedVirtualElement: columnMenuVirtualElement,
  options: {
    placement: 'top',
    offset: 8,
    flip: {},
  },
};

const tableCellsMergeMenuConfig = {
  pluginKey: 'tableCellsMergeMenu',
  shouldShow: ({ editor }) => willShowCellsMergeMenu(editor),
  element: buildHtmlElement('table-cells-merge-menu'),
  options: {
    placement: 'bottom',
    offset: 8,
    flip: {},
  },
};

const tableCellsSplitMenuConfig = {
  pluginKey: 'tableCellsSplitMenu',
  shouldShow: ({ editor }) => willShowCellsSplitMenu(editor),
  element: buildHtmlElement('table-cells-split-menu'),
  getReferencedVirtualElement: cellsSplitMenuVirtualElement,
  options: {
    placement: 'bottom',
    offset: 8,
    flip: {},
  },
};

const MouseTracker = Extension.create({
  name: 'MouseTracker',
  addStorage: () => ({ isMousePressed: false }),
  onCreate({ editor }) {
    const editorPage = editor.view.dom;

    const storeMousePressed = () => {
      editor.storage.isMousePressed = true;
    };

    const storeMouseReleaseAndForceRefresh = () => {
      editor.storage.isMousePressed = false;

      const { state, view } = editor;
      const anySelection = TextSelection.create(state.doc, state.selection.from);
      const unselect = state.tr.setSelection(anySelection);
      const restoreSelection = state.tr.setSelection(state.selection);

      view.dispatch(unselect);
      view.dispatch(restoreSelection);
    };

    editorPage.addEventListener('mousedown', storeMousePressed);
    editorPage.addEventListener('mouseup', storeMouseReleaseAndForceRefresh);
  },
});

// Adds a `backgroundColor` attribute to table cells/headers, persisted as an
// inline `background-color` style so it survives serialization to/from HTML.
const cellBackgroundAttributes = {
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) {
            return {};
          }
          return { style: `background-color: ${attributes.backgroundColor}` };
        },
      },
    };
  },
};

const TiptapTableCellWithBackground = TiptapTableCell.extend(cellBackgroundAttributes);
const TiptapTableHeaderWithBackground = TiptapTableHeader.extend(cellBackgroundAttributes);

const cellBackgroundColor = (editor) =>
  editor.getAttributes('tableCell').backgroundColor ||
  editor.getAttributes('tableHeader').backgroundColor ||
  null;

const normalizeColor = (color) => {
  if (!color) {
    return null;
  }
  const probe = document.createElement('span');
  probe.style.backgroundColor = color;
  return probe.style.backgroundColor || null;
};

const syncCellBackgroundMenu = ({ editor }) => {
  const toolbar = toolbarFor(editor);
  if (!toolbar) {
    return;
  }

  const control = toolbar.querySelector('[data-dropdown="cell-color"]');
  if (control) {
    control.classList.toggle('hide', !editingTable(editor));
  }

  const color = cellBackgroundColor(editor);
  const normalizedColor = normalizeColor(color);

  const currentSwatch = toolbar.querySelector('.cell-color-control__current');
  if (currentSwatch) {
    currentSwatch.classList.toggle('cell-color-control__current--none', !color);
    currentSwatch.style.backgroundColor = color || '';
  }

  const customInput = toolbar.querySelector('input[data-command="table:cells:background:set"]');
  if (customInput) {
    customInput.value = colorHex(color || '#000000').toLowerCase();
  }

  toolbar
    .querySelectorAll('button[data-command="table:cells:background:set"]')
    .forEach((swatch) => {
      const matches =
        normalizedColor !== null &&
        normalizeColor(swatch.getAttribute('value')) === normalizedColor;
      swatch.classList.toggle('active', matches);
    });
};

const setCellBackground =
  (color) =>
  ({ tr, state, dispatch }) => {
    const { selection } = state;
    const isCellSelection = selection instanceof CellSelection;
    let changed = false;

    isCellSelection &&
      selection.forEachCell((node, pos) => {
        if (node.attrs.backgroundColor !== color) {
          dispatch && tr.setNodeMarkup(pos, null, { ...node.attrs, backgroundColor: color });
          changed = true;
        }
      });
    return changed;
  };

const TableCellBackground = Extension.create({
  name: 'TableCellBackground',
  onCreate: syncCellBackgroundMenu,
  onSelectionUpdate: syncCellBackgroundMenu,
  onTransaction: syncCellBackgroundMenu,
  addCommands() {
    return {
      setCellBackgroundColor:
        (color) =>
        ({ tr, state, commands, dispatch }) =>
          state.selection instanceof CellSelection
            ? setCellBackground(color)({ tr, state, dispatch })
            : commands.setCellAttribute('backgroundColor', color),
      unsetCellBackgroundColor:
        () =>
        ({ tr, state, commands, dispatch }) =>
          state.selection instanceof CellSelection
            ? setCellBackground(null)({ tr, state, dispatch })
            : commands.setCellAttribute('backgroundColor', null),
    };
  },
});

const TableRowMenu = BubbleMenu.extend({ name: 'TableRowMenu' });
const TableColumnMenu = BubbleMenu.extend({ name: 'TableColumnMenu' });
const TableCellMergeMenu = BubbleMenu.extend({ name: 'TableCellMergeMenu' });
const TableCellSplitMenu = BubbleMenu.extend({ name: 'TableCellSplitMenu' });
const tableExtensions = [
  MouseTracker,
  TiptapTable.configure(tableConfiguration),
  TiptapTableCellWithBackground,
  TiptapTableHeaderWithBackground,
  TiptapTableRow,
  TableCellBackground,
  TableRowMenu.configure(tableRowsMenuConfig),
  TableColumnMenu.configure(tableColumnsMenuConfig),
  TableCellMergeMenu.configure(tableCellsMergeMenuConfig),
  TableCellSplitMenu.configure(tableCellsSplitMenuConfig),
];

export default tableExtensions;
